import { PickerPlan, PickerPremium, isPaidActiveSubscription } from '../../../lib/plans';
import { ScheduledChangeResp } from './settings-shared';
import { usePlans, usePremium, useScheduledChange } from './settings-queries';

export interface PremiumPlan {
  premium: PickerPremium | null;
  plans: PickerPlan[] | null;
  scheduled: ScheduledChangeResp | null;
  loaded: boolean;
  failed: boolean;
  /**
   * Whether there is a Stripe-backed subscription in OUR namespace. NOT the free/paid DISPLAY
   * decision: a user whose credits are funded by a Wix subscription reads as "paid"
   * (settings-plan → isFreePlan, from the legacy credit grant) while holding nothing of ours — so
   * anything that talks about *our* subscription (the scheduled-change reads and the notice they
   * drive) keys on this rather than on `!isFree`.
   */
  hasOurSubscription: boolean;
}

// Shared plan/subscription state for the settings surfaces, backed entirely by react-query:
// /api/premium (+ /api/plans for names, /api/premium/scheduled-change for a pending
// downgrade/cancellation) as cached reads. Each surface derives its own active `sub` from `premium`
// and renders its own layout.
//
// Settings is READ-ONLY about the subscription: cancelling a plan, and undoing a scheduled
// cancel/downgrade, both live on the packages page (SymphonyPricing's current-plan menu) — the same
// place the mobile card and both native apps send people via "Change".
export function usePremiumPlan(): PremiumPlan {
  const premiumQ = usePremium();
  const plansQ = usePlans();
  const hasPaidSub = (premiumQ.data?.subscriptions ?? []).some(isPaidActiveSubscription);
  const scheduledQ = useScheduledChange(hasPaidSub);

  return {
    premium: premiumQ.data ?? null,
    plans: plansQ.data ?? null,
    scheduled: scheduledQ.data ?? null,
    loaded: !premiumQ.isLoading && !plansQ.isLoading,
    failed: premiumQ.isError,
    hasOurSubscription: hasPaidSub,
  };
}
